<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import { RadioListI } from '@/typescript/types/ui-components/RadioItem'
  import { CommonErrorI } from '@/typescript/types/abstracts/errors/ErrorHandlerTypes'
  import { sanitizeHtml } from '@/utils'

  export default defineComponent({
    name: 'RadioItem',
    props: {
      htmlWrapperClass: {
        type: String,
        default: '',
      },
      modelValue: {
        type: [String, Number],
        default: 'card',
      },
      radioList: {
        type: Array as PropType<RadioListI[]>,
        default: () => [
          {
            id: 0,
            name: 'payment',
            value: 'card',
            checked: true,
            label: 'Credit card',
          },
          {
            id: 1,
            name: 'payment',
            value: 'paypal',
            checked: false,
            label: 'PayPal',
          },
        ],
      },
      checkboxStyle: {
        type: String as PropType<'circle' | 'square'>,
        default: 'circle',
        validate(val: string) {
          return ['circle', 'square'].includes(val)
        },
      },
      error: {
        type: [Object, String] as PropType<CommonErrorI | string | undefined | null>,
        default: () => ({}),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'clearErrors'],
    computed: {
      internalValue: {
        get() {
          return this.modelValue
        },
        set(v: any) {
          this.$emit('update:modelValue', v)
        },
      },
      getCheckboxStyle() {
        switch (this.checkboxStyle) {
          case 'circle':
            return 'circle-checkbox'
          case 'square':
            return 'square-checkbox'
          default:
            return 'circle-checkbox'
        }
      },
    },
    mounted() {
      if (this.radioList) {
        this.internalValue = this.radioList[0].value
      }
    },
    methods: {
      sanitizeHtml,
    },
  })
</script>

<template>
  <div :class="{ 'radio-item--disabled': disabled }">
    <div
      v-for="(radio, index) of radioList"
      :key="radio.id"
      class=""
      :class="index === radioList.length - 1 ? '' : 'mb-5'"
    >
      <div class="d-flex justify-start align-center">
        <input
          :id="`${radio.name}-${radio.value}`"
          v-model="internalValue"
          type="radio"
          :name="radio.name"
          :value="radio.value"
          :checked="radio.checked"
          :disabled="disabled"
          class="mr-5"
          :class="getCheckboxStyle"
          @click="$emit('clearErrors')"
        />
        <label v-if="radio.label" :for="`${radio.name}-${radio.value}`">
          <BaseIcon
            v-if="radio.icon"
            :icon-name="radio.icon"
            icon-color="black"
            width="20px"
            height="20px"
            class="mr-2"
          />
          {{ radio.label }}
        </label>
        <label v-if="radio.html" class="w-100" :for="`${radio.name}-${radio.value}`">
          <div class="d-flex">
            <BaseIcon
              v-if="radio.icon"
              :icon-name="radio.icon"
              icon-color="black"
              width="20px"
              height="20px"
              class="mr-2"
            />
            <!-- eslint-disable-next-line vue/no-v-html -- sanitized via sanitizeHtml() -->
            <div class="w-100" :class="`${htmlWrapperClass}`" v-html="sanitizeHtml(radio.html)" />
          </div>
        </label>
      </div>
      <Transition>
        <HasError
          v-if="error && Object.keys(error).length > 0 && internalValue === radio.value"
          :key="`hasError-${radio.name}`"
          :error="error"
        />
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
  input[type='radio'] {
    &.circle-checkbox {
      appearance: none;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      border: 2px solid var(--color-text-grey);
      transition: 0.2s all linear;

      &:checked {
        position: relative;
        background: var(--color-light-orange);
        border-color: transparent;

        &::before {
          content: '';
          display: block;
          border-radius: 50%;
          position: absolute;
          top: 1px;
          left: 1px;
          width: 14px;
          height: 14px;
          border: 2px var(--color-white) solid;
          box-sizing: border-box;
        }
      }
    }
    &.square-checkbox {
      appearance: none;
      width: 16px;
      height: 16px;
      min-width: 16px;
      background-color: var(--color-white);
      border: 1px solid var(--color-border-grey);
      border-radius: 2.5px;
      transition: 0.2s all linear;
      &:checked {
        background-color: var(--color-light-orange);
        border-color: var(--color-light-orange);
        position: relative;
        &::after {
          content: '';
          position: absolute;
          z-index: 3;
          width: 100%;
          height: 100%;
          left: 0;
          top: 0;
          background: url('@/assets/icons/icon-checklist.svg') no-repeat;
        }
      }
    }
  }
  .card_wrapper {
    display: flex;
    justify-content: space-between;
  }

  .radio-item--disabled {
    opacity: 0.6;

    input[type='radio'],
    label {
      cursor: not-allowed;
    }
  }
</style>
